/**
 * Pure landed cost logic (CC-6: NEVER use Lingui macros here).
 *
 * A landed cost allocates an ADDITIONAL cost (freight, customs, insurance)
 * across the products of a receipt (stock_picking), PROPORTIONALLY to the VALUE
 * of each product in that receipt. The allocation revalues inventory: for each
 * product, a VALUE-ONLY valuation layer is created (qty = 0) that increases
 * total value without touching on-hand → moving average cost increases.
 *
 * Quantities in INTEGERS (minor units, scale factor 1000); landed cost
 * amount and values are in minor units of currency.
 */

export type LandedCostState = "draft" | "done";

export const LANDED_COST_STATES: readonly LandedCostState[] = ["draft", "done"] as const;

export function isLandedCostState(value: string): value is LandedCostState {
  return (LANDED_COST_STATES as readonly string[]).includes(value);
}

export function coerceLandedCostState(value: string | null | undefined): LandedCostState {
  return value && isLandedCostState(value) ? value : "draft";
}

/** Can landed cost be applied? Only from draft. */
export function canApplyLandedCost(state: string | null | undefined): boolean {
  return coerceLandedCostState(state) === "draft";
}

/** Allocation base: product with VALUE contributed in the receipt. */
export interface LandedCostBase {
  productId: string;
  /** Product value in the receipt (minor units of currency, > 0). */
  receiptValue: number;
}

/** Portion of additional cost allocated to a product. */
export interface LandedCostAllocation {
  productId: string;
  /** Amount allocated to this product (minor units of currency). */
  amount: number;
}

/**
 * Allocates `amount` (total additional cost) over bases PROPORTIONALLY to their
 * `receiptValue`. Uses the largest remainder method so the sum of portions
 * equals EXACTLY `amount` (without losing/gaining cents to rounding).
 *
 * If the sum of base values is 0 (receipt with no value), allocates equally
 * among bases. Deterministic: input order breaks ties.
 */
export function allocateLandedCost(
  amount: number,
  bases: LandedCostBase[],
): LandedCostAllocation[] {
  if (bases.length === 0 || amount === 0) {
    return bases.map((b) => ({ productId: b.productId, amount: 0 }));
  }

  const totalBase = bases.reduce((acc, b) => acc + Math.max(b.receiptValue, 0), 0);

  // Exact allocation on integer basis: floor of each portion + remainder allocation.
  // `frac` (fractional part) decides who gets the leftover units.
  const rows = bases.map((b, i) => {
    const weight = totalBase > 0 ? Math.max(b.receiptValue, 0) / totalBase : 1 / bases.length;
    const exact = amount * weight;
    const floor = Math.floor(exact);
    return { productId: b.productId, index: i, amount: floor, frac: exact - floor };
  });
  let remainder = amount - rows.reduce((acc, r) => acc + r.amount, 0);

  // Assign remaining units to portions with largest fraction (original index
  // breaks ties to remain deterministic).
  for (const row of rows.toSorted((a, b) => b.frac - a.frac || a.index - b.index)) {
    if (remainder <= 0) {
      break;
    }
    row.amount += 1;
    remainder -= 1;
  }
  return rows.map((r) => ({ productId: r.productId, amount: r.amount }));
}

/**
 * New unit cost following a revaluation (landed cost): the extra value
 * allocated is distributed over the product's on-hand.
 *
 *   newAvg = ((onHand × oldAvg / QTY_SCALE) + extraValue) × QTY_SCALE / onHand
 *
 * Useful for previewing/reporting the effect. `onHand` is scaled ×1000;
 * `oldAvg`/result are per whole unit. Returns oldAvg if onHand <= 0.
 */
export function revaluedUnitCost(
  onHand: number,
  oldAvg: number,
  extraValue: number,
  qtyScale: number,
): number {
  if (onHand <= 0) {
    return oldAvg;
  }
  const oldValue = Math.round((onHand * oldAvg) / qtyScale);
  const newValue = oldValue + extraValue;
  return Math.round((newValue * qtyScale) / onHand);
}
